import type { ReactNode } from 'react';
import { formatMoney } from '../format';
import type { PaymentRecordSummary } from '../types';
import { UsersIcon, WalletIcon, WarnIcon, XCircleIcon } from './icons';

function Card({ tone, icon, label, value, note }: { tone: string; icon: ReactNode; label: string; value: string; note?: string | undefined }) {
  return (
    <div className={`cp-kpi cp-kpi--${tone}`}>
      <span className="cp-kpi__icon" aria-hidden="true">{icon}</span>
      <dt className="cp-kpi__label">{label}</dt>
      <dd className="cp-kpi__value">{value}</dd>
      {note && <dd className="cp-kpi__note">{note}</dd>}
    </div>
  );
}

/**
 * The four cards of the Figma list (350 x 85 each). Every value is the server's, for the whole company and the shown year. Differences from the Figma

 * wording is deliberate: "Total Net Pay (YTD)" is "Total Contractor Payments (YTD)" (a contractor payment has no net pay). The 1099 card shows both the
 * approaching count (the Figma label) and the reached count; the percent is the company's setting or the server default (COMPLIANCE RULE CONFIRMATION REQUIRED).
 */
export function KpiCards({ summary }: { summary: PaymentRecordSummary }) {
  const configured = summary.form1099ThresholdConfigured;
  return (
    <dl className="cp-kpis" aria-label="Contractor payment summary">
      <Card tone="total" icon={<WalletIcon />} label="Total Contractor Payments (YTD)" value={formatMoney(summary.totalContractorPaymentsYtd)} note={`${summary.year}, excludes voided`} />
      <Card tone="active" icon={<UsersIcon />} label="Active Contractors" value={String(summary.activeContractors)} />
      <Card
        tone="threshold" icon={<WarnIcon />} label="Approaching 1099 Threshold" value={String(summary.thresholdApproachingCount)}
        note={configured ? `${summary.thresholdReachedCount} reached · threshold ${formatMoney(summary.form1099ThresholdAmount)}${summary.form1099ApproachingPercent !== null ? ` · from ${summary.form1099ApproachingPercent}%` : ''}` : 'Threshold not set'}
      />
      <Card tone="w9" icon={<XCircleIcon />} label="Missing W-9 on File" value={String(summary.missingW9)} />
    </dl>
  );
}

export function KpiSkeleton({ message }: { message: string }) {
  return <p className="cp-muted" role="status" data-testid="kpi-status">{message}</p>;
}
